import type { RoundTypeHandler } from './RoundTypeHandler'

/**
 * Central registry for all round type handlers
 * Provides a single source of truth for available round types
 */
class RoundTypeRegistry {
  private handlers = new Map<string, RoundTypeHandler>()

  /**
   * Register a new round type handler
   * @param handler Round type handler to register
   * @throws Error if handler with same type already registered
   */
  register(handler: RoundTypeHandler): void {
    if (this.handlers.has(handler.type)) {
      console.warn(`Round type handler '${handler.type}' is already registered. Overwriting.`)
    }
    this.handlers.set(handler.type, handler)
    console.log(`✓ Registered round type: ${handler.type} (${handler.label})`)
  }

  /**
   * Get handler for a specific round type
   * @param type Round type identifier
   * @returns Handler or undefined if not found
   */
  get(type: string): RoundTypeHandler | undefined {
    return this.handlers.get(type)
  }

  /**
   * Check if a round type is registered
   * @param type Round type identifier
   * @returns True if registered
   */
  has(type: string): boolean {
    return this.handlers.has(type)
  }

  /**
   * Get all registered handlers
   * @returns Array of all registered handlers
   */
  getAll(): RoundTypeHandler[] {
    return Array.from(this.handlers.values())
  }

  /**
   * Get all registered round type identifiers
   * @returns Array of type identifiers
   */
  getTypes(): string[] {
    return Array.from(this.handlers.keys())
  }

  /**
   * Unregister a round type handler (useful for testing)
   * @param type Round type identifier
   * @returns True if handler was removed
   */
  unregister(type: string): boolean {
    return this.handlers.delete(type)
  }

  /**
   * Clear all registered handlers (useful for testing)
   */
  clear(): void {
    this.handlers.clear()
  }

  /**
   * Get count of registered handlers
   */
  get size(): number {
    return this.handlers.size
  }
}

// Export singleton instance
export const roundTypeRegistry = new RoundTypeRegistry()

// Export class for testing
export { RoundTypeRegistry }

/**
 * Helper to get handler with fallback
 */
export function getRoundTypeHandlerSafe(type: string | undefined): RoundTypeHandler | null {
  if (!type) {
    console.warn('getRoundTypeHandlerSafe: No round type provided')
    return null
  }

  const handler = roundTypeRegistry.get(type)
  if (!handler) {
    console.warn(`getRoundTypeHandlerSafe: No handler found for round type '${type}'`)
    return null
  }

  return handler
}

/**
 * Helper to check if capabilities are supported
 */
export function supportsCapability(
  type: string | undefined,
  capability: keyof RoundTypeHandler['capabilities']
): boolean {
  const handler = getRoundTypeHandlerSafe(type)
  return handler?.capabilities[capability] ?? false
}
